import React, { useState } from 'react';
import { FileText, Download, Calendar, Award, Building, User, Mail, Phone, Info, AlertCircle } from 'lucide-react';

const Certificates = () => {
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  // Mock data since no APIs exist for certificates
  const certificates = [
    {
      id: 1,
      type: 'Form 16',
      title: 'Annual Tax Certificate',
      financial_year: '2024-25',
      status: 'available',
      generated_date: '2024-05-15',
      description: 'Certificate of tax deducted at source for FY 2024-25'
    },
    {
      id: 2,
      type: 'Salary Certificate',
      title: 'Monthly Salary Certificate',
      financial_year: '2024-25',
      status: 'available',
      generated_date: '2024-12-01',
      description: 'Certificate showing current monthly salary details'
    },
    {
      id: 3,
      type: 'Experience Letter',
      title: 'Employment Experience Certificate',
      financial_year: '2024-25',
      status: 'request_required',
      generated_date: null,
      description: 'Certificate of employment and experience duration'
    },
    {
      id: 4,
      type: 'Relieving Letter',
      title: 'Employment Relieving Certificate',
      financial_year: '2024-25',
      status: 'not_applicable',
      generated_date: null,
      description: 'Certificate issued upon employment termination'
    }
  ];

  const employeeInfo = {
    name: 'John Doe',
    employee_id: 'EMP001',
    designation: 'Senior Software Engineer',
    department: 'Engineering',
    joining_date: '2022-03-15',
    email: 'john.doe@company.com',
    phone: '+91 98765 43210'
  };

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - 4 + i);

  const getStatusInfo = (status) => {
    switch (status) {
      case 'available':
        return { color: 'bg-green-100 text-green-800', text: 'Available', icon: FileText };
      case 'request_required':
        return { color: 'bg-yellow-100 text-yellow-800', text: 'Request Required', icon: Mail };
      case 'processing':
        return { color: 'bg-blue-100 text-blue-800', text: 'Processing', icon: Calendar };
      case 'not_applicable':
        return { color: 'bg-gray-100 text-gray-800', text: 'Not Applicable', icon: AlertCircle };
      default:
        return { color: 'bg-gray-100 text-gray-800', text: 'Unknown', icon: FileText };
    }
  };

  const downloadCertificate = (certId, certType) => {
    // Mock download - in real app, this would call an API
    alert(`Downloading ${certType}... (API not implemented)`);
  };

  const requestCertificate = (certType) => {
    alert(`Request submitted for ${certType}. HR will process your request within 3-5 business days.`);
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Form 16 & Certificates</h1>
          <p className="text-gray-600 mt-2">Access your tax certificates, salary certificates, and employment documents</p>
        </div>

        {/* Employee Information Card */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <User className="h-5 w-5 mr-2" />
            Employee Information
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <p className="text-sm text-gray-600">Name:</p>
              <p className="font-medium text-gray-900">{employeeInfo.name}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Employee ID:</p>
              <p className="font-medium text-gray-900">{employeeInfo.employee_id}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Designation:</p>
              <p className="font-medium text-gray-900">{employeeInfo.designation}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Joining Date:</p>
              <p className="font-medium text-gray-900">{new Date(employeeInfo.joining_date).toLocaleDateString()}</p>
            </div>
          </div>
        </div>

        {/* Year Selection */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Calendar className="h-5 w-5 text-blue-500 mr-2" />
              <span className="text-lg font-semibold text-gray-900">Financial Year</span>
            </div>
            <select
              className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
            >
              {years.map(year => (
                <option key={year} value={year}>FY {year}-{year + 1}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Certificates Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {certificates.map((certificate) => {
            const statusInfo = getStatusInfo(certificate.status);
            const StatusIcon = statusInfo.icon;
            
            return (
              <div key={certificate.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center">
                    <div className="bg-blue-100 p-3 rounded-lg mr-4">
                      <FileText className="h-6 w-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">{certificate.title}</h3>
                      <p className="text-sm text-gray-600">{certificate.type}</p>
                    </div>
                  </div>
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${statusInfo.color}`}>
                    {statusInfo.text}
                  </span>
                </div>

                <p className="text-sm text-gray-700 mb-4">{certificate.description}</p>

                <div className="space-y-2 mb-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Financial Year:</span>
                    <span className="font-medium text-gray-900">{certificate.financial_year}</span>
                  </div>
                  {certificate.generated_date && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Generated On:</span>
                      <span className="text-gray-900">{new Date(certificate.generated_date).toLocaleDateString()}</span>
                    </div>
                  )}
                </div>

                <div className="flex space-x-2">
                  {certificate.status === 'available' && (
                    <>
                      <button
                        onClick={() => downloadCertificate(certificate.id, certificate.type)}
                        className="flex-1 bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 flex items-center justify-center"
                      >
                        <Download className="h-4 w-4 mr-2" />
                        Download
                      </button>
                      <button className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 flex items-center justify-center">
                        <Mail className="h-4 w-4 mr-2" />
                        Email
                      </button>
                    </>
                  )}
                  
                  {certificate.status === 'request_required' && (
                    <button
                      onClick={() => requestCertificate(certificate.type)}
                      className="w-full bg-yellow-600 text-white px-4 py-2 rounded-md hover:bg-yellow-700 flex items-center justify-center"
                    >
                      <Mail className="h-4 w-4 mr-2" />
                      Request Certificate
                    </button>
                  )}
                  
                  {certificate.status === 'not_applicable' && (
                    <button
                      disabled
                      className="w-full bg-gray-300 text-gray-500 px-4 py-2 rounded-md cursor-not-allowed flex items-center justify-center"
                    >
                      <StatusIcon className="h-4 w-4 mr-2" />
                      Not Applicable
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Form 16 Information */}
        {/* <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Award className="h-5 w-5 mr-2 text-purple-500" />
            About Form 16
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-medium text-gray-900 mb-3">What is Form 16?</h4>
              <div className="text-sm text-gray-700 space-y-2">
                <p>Form 16 is a certificate issued by your employer showing:</p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Total salary paid during the financial year</li>
                  <li>Tax deducted at source (TDS)</li>
                  <li>Employee and employer details</li>
                  <li>Quarterly TDS details</li>
                </ul>
              </div>
            </div>
            <div>
              <h4 className="font-medium text-gray-900 mb-3">When is it Available?</h4>
              <div className="text-sm text-gray-700 space-y-2">
                <p>Form 16 is typically available:</p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>After May 15th for the previous financial year</li>
                  <li>Once employer files quarterly TDS returns</li>
                  <li>After salary processing is complete</li>
                  <li>Following final tax calculations</li>
                </ul>
              </div>
            </div>
          </div>
        </div> */}

        {/* Request Process */}
        {/* <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-blue-900 mb-3 flex items-center">
            <Mail className="h-5 w-5 mr-2" />
            Certificate Request Process
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-blue-800">
            <div className="text-center">
              <div className="bg-blue-200 rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-2">
                <span className="font-bold text-blue-800">1</span>
              </div>
              <p className="font-medium">Submit Request</p>
              <p className="text-xs mt-1">Click request button for required certificate</p>
            </div>
            <div className="text-center">
              <div className="bg-blue-200 rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-2">
                <span className="font-bold text-blue-800">2</span>
              </div>
              <p className="font-medium">HR Processing</p>
              <p className="text-xs mt-1">HR team processes your request (3-5 business days)</p>
            </div>
            <div className="text-center">
              <div className="bg-blue-200 rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-2">
                <span className="font-bold text-blue-800">3</span>
              </div>
              <p className="font-medium">Download</p>
              <p className="text-xs mt-1">Certificate becomes available for download</p>
            </div>
          </div>
        </div> */}

        {/* No API Available Notice */}
        {/* <div className="mt-6 bg-red-50 border border-red-200 rounded-lg p-6">
          <div className="flex items-center">
            <Info className="h-5 w-5 text-red-500 mr-3" />
            <div>
              <h3 className="text-lg font-semibold text-red-900">API Integration Required</h3>
              <p className="text-red-800 mt-2">
                This page displays mock data. The following APIs need to be implemented:
              </p>
              <ul className="list-disc list-inside text-red-700 mt-2 text-sm">
                <li><code>GET /api/certificates</code> - Fetch available certificates</li>
                <li><code>GET /api/certificates/{id}/download</code> - Download certificates</li>
                <li><code>POST /api/certificates/request</code> - Request new certificates</li>
                <li><code>GET /api/certificates/form16/{year}</code> - Get Form 16</li>
              </ul>
            </div>
          </div>
        </div> */}
      </div>
    </div>
  );
};

export default Certificates;